import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as featureActions from './feature.actions';
import { loadingStarted, loadingEnded } from 'src/app/store/state/loading/loading.actions';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { FeatureApiService } from 'src/app/core/services/feature/feature-api.service';
import { NotificationService } from 'src/app/shared/services/notifier';
import { tap } from 'rxjs/operators';
import { FeatureViewModel } from 'src/app/data/models/feature/feature.model';

@Injectable()
export class FeatureEffects {
    loadAll$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadAllFeatures),
        map(() => loadingStarted())
    ));

    loadAllInvoke$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadAllFeatures),
        switchMap(() => this.api.getAllFeatures().pipe(
            map((features: FeatureViewModel[]) => featureActions.loadAllFeaturesSuccess({ features })),
            catchError(error => of(featureActions.loadAllFeaturesFailure({ error })))
        ))
    ));

    loadUserStart$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadUserFeatures),
        map(() => loadingStarted())
    ));

    loadUser$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadUserFeatures),
        mergeMap(({ userId }) => this.api.getUserFeatures(userId).pipe(
            map((f: FeatureViewModel[]) => featureActions.loadUserFeaturesSuccess({ userId, featureIds: f.map(x => x.FeatureId) })),
            catchError(error => of(featureActions.loadUserFeaturesFailure({ userId, error })))
        ))
    ));

    updateUserStart$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.updateUserFeatures),
        map(() => loadingStarted())
    ));

    updateUser$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.updateUserFeatures),
        mergeMap(({ userId, featureIds }) => this.api.setUserFeatures(userId, featureIds).pipe(
            map(() => featureActions.updateUserFeaturesSuccess({ userId, featureIds })),
            catchError(error => of(featureActions.updateUserFeaturesFailure({ userId, error })))
        ))
    ));

    // Loading end hooks (success/failure both decrement)
    loadAllEnd$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadAllFeaturesSuccess, featureActions.loadAllFeaturesFailure),
        map(() => loadingEnded())
    ));

    loadUserEnd$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadUserFeaturesSuccess, featureActions.loadUserFeaturesFailure),
        map(() => loadingEnded())
    ));

    updateUserEnd$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.updateUserFeaturesSuccess, featureActions.updateUserFeaturesFailure),
        map(() => loadingEnded())
    ));

    // Toast notifications (side-effects only)
    loadUserFailureToast$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.loadUserFeaturesFailure),
        tap(({ error }) => this.notify.error(error?.error || 'Failed to load user features.'))
    ), { dispatch: false });

    updateSuccessToast$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.updateUserFeaturesSuccess),
        tap(() => this.notify.success('User features saved successfully.'))
    ), { dispatch: false });

    updateFailureToast$ = createEffect(() => this.actions$.pipe(
        ofType(featureActions.updateUserFeaturesFailure),
        tap(({ error }) => this.notify.error(error?.error || 'Save failed.'))
    ), { dispatch: false });

    constructor(private actions$: Actions, private api: FeatureApiService, private notify: NotificationService) { }
}
